/**
 * ImportPreviewTable (FE-4, PHASE1 §7.4). Shows the staged rows with dedup
 * badges + counts. `new` rows are selected by default (uncheck to exclude);
 * `possible_duplicate` rows are UNCHECKED by default (check to include);
 * `duplicate` / `ignored` rows are never imported. Commit sends the checked
 * possibles as `include_possible_ids` and the unchecked news as
 * `exclude_row_indexes` (both keyed by `row_index`).
 */
import { useMemo, useState } from 'react';
import { Button } from '../../components/ui/Button.tsx';
import { AmountText } from '../../components/ui/AmountText.tsx';
import { DedupBadge } from '../../components/ui/DedupBadge.tsx';
import { useToast } from '../../context/ToastContext.tsx';
import { useCommitImport, useDiscardImport } from '../../lib/hooks/useImport.ts';
import { formatDate } from '../../lib/format.ts';
import { BANK_PROFILE } from '../../lib/labels.ts';
import type { ImportCommitOut, ImportPreviewOut, ImportPreviewRow } from '../../lib/types.ts';

function isSelectable(row: ImportPreviewRow): boolean {
  return row.dedup === 'new' || row.dedup === 'possible_duplicate';
}

export interface ImportPreviewTableProps {
  preview: ImportPreviewOut;
  onCommit: (result: ImportCommitOut) => void;
  onDiscard: () => void;
  onBack: () => void;
}

export function ImportPreviewTable({
  preview,
  onCommit,
  onDiscard,
  onBack,
}: ImportPreviewTableProps) {
  const { toast } = useToast();
  const commit = useCommitImport();
  const discard = useDiscardImport();

  // possible_duplicate row_indexes the user opted IN.
  const [includePossible, setIncludePossible] = useState<Set<number>>(() => new Set());
  // `new` row_indexes the user opted OUT.
  const [excludeNew, setExcludeNew] = useState<Set<number>>(() => new Set());

  const { counts, rows } = preview;

  const toImport = useMemo(
    () => counts.new - excludeNew.size + includePossible.size,
    [counts.new, excludeNew.size, includePossible.size],
  );

  function toggle(row: ImportPreviewRow, checked: boolean) {
    if (row.dedup === 'possible_duplicate') {
      setIncludePossible((prev) => {
        const next = new Set(prev);
        if (checked) next.add(row.row_index);
        else next.delete(row.row_index);
        return next;
      });
    } else if (row.dedup === 'new') {
      setExcludeNew((prev) => {
        const next = new Set(prev);
        if (checked) next.delete(row.row_index);
        else next.add(row.row_index);
        return next;
      });
    }
  }

  function isChecked(row: ImportPreviewRow): boolean {
    if (row.dedup === 'possible_duplicate') return includePossible.has(row.row_index);
    if (row.dedup === 'new') return !excludeNew.has(row.row_index);
    return false;
  }

  function runCommit() {
    commit.mutate(
      {
        importId: preview.import_id,
        body: {
          include_possible_ids: [...includePossible],
          exclude_row_indexes: [...excludeNew],
        },
      },
      {
        onSuccess: (result) => onCommit(result),
        onError: (err) => toast(err.message, { variant: 'error' }),
      },
    );
  }

  function runDiscard() {
    discard.mutate(preview.import_id, {
      onSuccess: () => {
        toast('Import verworfen.', { variant: 'info' });
        onDiscard();
      },
      onError: (err) => toast(err.message, { variant: 'error' }),
    });
  }

  const busy = commit.isPending || discard.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {BANK_PROFILE[preview.profile]}
        </span>
        <span className="flex flex-wrap gap-2 text-xs">
          <Count label="Gesamt" value={counts.total} />
          <Count label="Neu" value={counts.new} tone="emerald" />
          <Count label="Duplikat" value={counts.duplicate} tone="slate" />
          <Count label="Möglich" value={counts.possible_duplicate} tone="amber" />
          <Count label="Ignoriert" value={counts.ignored} tone="slate" />
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th className="w-10 border-b border-slate-100 px-3 py-2.5 dark:border-slate-800/70" />
              <th className="border-b border-slate-100 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:border-slate-800/70 dark:text-slate-500">
                Datum
              </th>
              <th className="border-b border-slate-100 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:border-slate-800/70 dark:text-slate-500">
                Empfänger
              </th>
              <th className="border-b border-slate-100 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:border-slate-800/70 dark:text-slate-500">
                Verwendungszweck
              </th>
              <th className="border-b border-slate-100 px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:border-slate-800/70 dark:text-slate-500">
                Betrag
              </th>
              <th className="border-b border-slate-100 px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400 dark:border-slate-800/70 dark:text-slate-500">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/70">
            {rows.map((row) => (
              <tr
                key={row.row_index}
                className={`transition-colors duration-150 ${
                  row.parse_error
                    ? 'bg-rose-50/60 dark:bg-rose-950/30'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'
                }`}
              >
                <td className="px-3 py-3.5 align-top">
                  {isSelectable(row) ? (
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border border-slate-200 accent-sky-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600 dark:border-slate-700 dark:accent-sky-500"
                      checked={isChecked(row)}
                      onChange={(e) => toggle(row, e.target.checked)}
                      aria-label="Für Import auswählen"
                    />
                  ) : null}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 align-top tabular-nums text-slate-500 dark:text-slate-500">
                  {row.booking_date ? formatDate(row.booking_date) : '—'}
                </td>
                <td className="px-3 py-3.5 align-top font-medium text-slate-900 dark:text-slate-100">
                  {row.counterparty_name ?? '—'}
                </td>
                <td className="max-w-xs px-3 py-3.5 align-top text-slate-600 dark:text-slate-400">
                  <span className="line-clamp-2">{row.purpose ?? '—'}</span>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5 text-right align-top tabular-nums">
                  {row.amount !== null ? <AmountText value={row.amount} /> : '—'}
                </td>
                <td className="px-3 py-3.5 align-top">
                  {row.parse_error ? (
                    <span className="text-xs text-rose-600 dark:text-rose-400">
                      {row.parse_error}
                    </span>
                  ) : (
                    <DedupBadge outcome={row.dedup} />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {toImport}
          </span>{' '}
          Umsätze werden importiert.
        </p>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={onBack} disabled={busy}>
            Zurück
          </Button>
          <Button variant="danger" onClick={runDiscard} disabled={busy}>
            {discard.isPending ? 'Verwerfe …' : 'Verwerfen'}
          </Button>
          <Button onClick={runCommit} disabled={busy || toImport <= 0}>
            {commit.isPending ? 'Importiere …' : 'Importieren'}
          </Button>
        </div>
      </div>
    </div>
  );
}

type CountTone = 'emerald' | 'amber' | 'slate';

// Solid pastel pills per DESIGN.md §3.11.
const TONE: Record<CountTone, string> = {
  emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  slate: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

function Count({ label, value, tone }: { label: string; value: number; tone?: CountTone }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-semibold tabular-nums ${
        tone ? TONE[tone] : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
      }`}
    >
      {label}: {value}
    </span>
  );
}
